import './rater.endorsements.list.scss';

import { CoverageUuid, EndorsementUuid } from '@converge/data';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  IUnderwritingEndorsement,
  QuoteLineItemFeatures,
  QuoteLineItemType,
  SaveAnswerInput,
  useGetEndorsementsQuery,
} from '@converge/graphql';
import { TableCell, TableRow } from '@mui/material';
import { sortByName, sortByOrder } from '@converge/utils';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../data';

import { FormSearchableSelectField } from '../../../../presentation';
import { RaterEndorsementListDetail } from './rater.endorsements.list.detail';
import { RaterQuoteSaveDialog } from '../common';
import { RaterTableTitleCell } from '../rater.content.table';
import { useTranslation } from 'react-i18next';

export const RaterEndorsementList = ({
  mode = 'primary',
}: {
  mode: 'primary' | 'excess';
}) => {
  const { t } = useTranslation();

  const { actions, dispatch } = useRaterActionDispatchContext();
  const {
    quoteEndorsements,
    coverages,
    columnCount,
    isEditable,
    upsertIsLoading,
    endoresementAnswers,
    quote: { inForcePolicy, limit, retention },
  } = useRaterStateContextSelector(
    ({
      quote: { endorsements: quoteEndorsements, coverages },
      columnCount,
      isEditable,
      upsertQuoteIsLoading,
      upsertAnswersIsLoading,
      endoresementAnswers,
      quote,
    }) => {
      return {
        quoteEndorsements,
        coverages,
        columnCount,
        isEditable,
        upsertIsLoading: upsertAnswersIsLoading || upsertQuoteIsLoading,
        endoresementAnswers,
        quote,
      };
    }
  );
  const [openSaveDialog, setOpenSaveDialog] = useState<boolean>(false);
  const [lastToggledUuid, setLastToggledUuid] = useState<string | undefined>();

  const updateAnswers = (
    quoteuuid: string | null | undefined,
    updatedAnswers: SaveAnswerInput[]
  ) => {
    if (quoteuuid)
      dispatch(
        actions.setEndoresementAnswers({
          ...endoresementAnswers,
          [quoteuuid]: updatedAnswers,
        })
      );
  };
  const uuid = useRaterStateContextSelector(({ submission: { uuid } }) => uuid);

  const { loading, endorsements: fullEndorsementsList } =
    useGetEndorsementsQuery({
      filters: { submissionUuid: uuid },
      pagination: null,
      sort: {
        key: 'name',
        order: 'ASC',
      },
    });

  const parentCoverage = useCallback(
    (endorsementCoverageUuid: string) =>
      coverages?.find((coverage) => coverage.uuid === endorsementCoverageUuid),
    [coverages]
  );

  const endorsements = useMemo(() => {
    return [...(quoteEndorsements ?? [])]
      .sort(sortByOrder)
      .filter((endorsement) =>
        mode === 'primary' ? !!endorsement.primary : !!endorsement.excess
      );
  }, [mode, quoteEndorsements]);

  const nspRetention =
    coverages?.find(
      (c) => c.uuid === CoverageUuid.NetworkSecurityPrivacyLiability
    )?.quote.retention ?? 0;

  useEffect(() => {
    const trackingTech = endorsements.find(
      (e) =>
        e.uuid ===
        EndorsementUuid.TrackingTechnologyLiabilityCoverageEndorsement
    );
    if (!trackingTech) return;

    const newRetention = Math.max(25000, nspRetention);
    if (trackingTech.quote?.retention === newRetention) return;

    dispatch(
      actions.updateEndorsement({
        ...trackingTech,
        quote: {
          ...trackingTech.quote,
          retention: newRetention,
        },
        update: true,
      })
    );
    // Only re-run when NSP retention changes, not when the user edits tech liability retention.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nspRetention]);

  const hasTrackingTechLiability = endorsements.some(
    (e) =>
      e.uuid ===
        EndorsementUuid.TrackingTechnologyLiabilityCoverageEndorsement &&
      e.quote?.included
  );

  const hasTrackingTechExpense = endorsements.some(
    (e) =>
      e.uuid === EndorsementUuid.TrackingTechnologyClaimsExpenseSublimit &&
      e.quote?.included
  );

  const trackingConflict = hasTrackingTechLiability && hasTrackingTechExpense;

  useEffect(() => {
    dispatch(actions.setTrackingConflict(trackingConflict));
  }, [trackingConflict, dispatch, actions]);

  const fullEndorsementsListForMode = useMemo(() => {
    return fullEndorsementsList
      .sort(sortByName)
      .filter(
        (endorsement) =>
          (!endorsement.coverageUuid ||
            (parentCoverage(endorsement.coverageUuid) &&
              parentCoverage(endorsement.coverageUuid)?.quote.included)) &&
          (mode === 'primary' ? !!endorsement.primary : !!endorsement.excess) &&
          !endorsements.some(({ uuid }) => endorsement.uuid === uuid)
      );
  }, [endorsements, fullEndorsementsList, mode, parentCoverage]);

  const limitAndRetentionCoverage =
    coverages?.find(
      (coverage) =>
        coverage.uuid === CoverageUuid.NetworkSecurityPrivacyLiability
    ) ?? coverages?.[0];

  const setNewEndorsementLimit = (endorsementUuid: string) => {
    const cyberCrimeCoverage =
      coverages?.find(
        (coverage) => coverage.uuid === CoverageUuid.CyberCrime
      ) ?? coverages?.[0];

    return endorsementUuid ===
      EndorsementUuid.AdditionalPrivacyEventExpensesOutSideLimit
      ? 1000000
      : endorsementUuid ===
        EndorsementUuid.ContingentBodilyInjuryAndPropertyDamage
      ? 250000
      : endorsementUuid === EndorsementUuid.BipaClaimsExpensesSublimit ||
        endorsementUuid === EndorsementUuid.TcpaClaimsExpensesSublimit ||
        endorsementUuid === EndorsementUuid.AdaClaimsExpensesSublimit
      ? 25000
      : endorsementUuid === EndorsementUuid.CyberEventAmendatory
      ? cyberCrimeCoverage?.quote.limit ?? 0
      : endorsementUuid === EndorsementUuid.MissedBidAmendatory ||
        endorsementUuid ===
          EndorsementUuid.TrackingTechnologyClaimsExpenseSublimit
      ? 50000
      : limitAndRetentionCoverage?.quote.limit ?? 0;
  };

  const setNewEndorsementRetention = (
    endorsementUuid: string,
    endorsementCoverageUuid: string
  ) => {
    const defaultRetention = limitAndRetentionCoverage?.quote.retention ?? 0;
    if (
      endorsementUuid ===
      EndorsementUuid.TrackingTechnologyLiabilityCoverageEndorsement
    ) {
      const nspRetention =
        coverages?.find(
          (c) => c.uuid === CoverageUuid.NetworkSecurityPrivacyLiability
        )?.quote.retention ?? 0;
      return Math.max(25000, nspRetention);
    }

    if (
      endorsementUuid === EndorsementUuid.MissedBidAmendatory ||
      endorsementUuid ===
        EndorsementUuid.TrackingTechnologyClaimsExpenseSublimit ||
      endorsementUuid === EndorsementUuid.CyberEventAmendatory
    ) {
      if (endorsementCoverageUuid) {
        return parentCoverage(endorsementCoverageUuid)?.quote.retention ?? 0;
      }
    }

    return defaultRetention;
  };

  return (
    <Fragment>
      <TableRow className="endorsements-header-container">
        <RaterTableTitleCell columnCount={columnCount}>
          Endorsements
        </RaterTableTitleCell>
      </TableRow>

      {[...endorsements].map((endorsement, index) => {
        if (endorsement.uuid) {
          return (
            <Fragment key={endorsement.uuid}>
              <RaterEndorsementListDetail
                mode={mode}
                quoteLimit={limit}
                quoteRetention={retention}
                endorsement={endorsement}
                setOpenSaveDialog={setOpenSaveDialog}
                answers={endoresementAnswers[endorsement.quote.uuid || '']}
                updateAnswers={updateAnswers}
                coverages={coverages}
                isEditable={isEditable && !upsertIsLoading}
                isLastRow={
                  coverages.length + index ===
                  coverages.length + endorsements.length - 1
                }
                parentCoverage={parentCoverage}
                conflictError={
                  trackingConflict && endorsement.uuid === lastToggledUuid
                    ? endorsement.uuid ===
                      EndorsementUuid.TrackingTechnologyClaimsExpenseSublimit
                      ? 'This endorsement cannot be on the same policy as <strong>Tracking Technology Liability Coverage Endorsement</strong>. Please select only one.'
                      : 'This endorsement cannot be on the same policy as <strong>Tracking Technology Claims Expense Sublimit</strong>. Please select only one.'
                    : undefined
                }
                onToggleIncluded={(toggledUuid, included) => {
                  if (included) setLastToggledUuid(toggledUuid);
                  else setLastToggledUuid(undefined);
                }}
              />
            </Fragment>
          );
        }

        return (
          <TableRow key={index}>
            <TableCell align="center" colSpan={columnCount}>
              No endorsements
            </TableCell>
          </TableRow>
        );
      })}

      {fullEndorsementsListForMode.length > 0 && (
        <TableRow>
          <TableCell colSpan={3}>
            <FormSearchableSelectField
              minDropdownWidth="350px"
              maxDropdownWidth="450px"
              disabled={upsertIsLoading || !isEditable || inForcePolicy}
              options={fullEndorsementsListForMode}
              isLoading={loading}
              mapToOption={(addEndorsement) => {
                return {
                  value: addEndorsement.uuid ?? '',
                  label: addEndorsement.name ?? 'N/A',
                };
              }}
              resetAfterSelect
              name=""
              placeholder="Select endorsement to add"
              onChangeOption={(option) => {
                const data = option.data;
                const { quote, ...endorsementProps } = data;

                if (endorsementProps.uuid) {
                  const newEndorsement: Omit<
                    IUnderwritingEndorsement,
                    'features'
                  > = {
                    ...endorsementProps,

                    questions: {
                      edges: endorsementProps.questions.edges?.map(
                        ({ node, ...etc }) => ({
                          ...etc,
                          node: {
                            ...node,
                            parent: `${node.parent}` === `true`,
                          },
                        })
                      ),
                    },
                    quote: {
                      ...quote,
                      included: mode === 'primary',
                      includedInExcess: mode === 'excess',
                      // TODO: Remove Fake data when API implemented properly:
                      limit: setNewEndorsementLimit(endorsementProps.uuid),
                      retention: setNewEndorsementRetention(
                        endorsementProps.uuid,
                        endorsementProps.coverageUuid || ''
                      ),
                      hazardGroup: null,
                      basePremium:
                        limitAndRetentionCoverage?.quote.basePremium ?? 0,
                      technicalPremium:
                        limitAndRetentionCoverage?.quote.technicalPremium ?? 0,
                      modifiedCoveragePremium:
                        limitAndRetentionCoverage?.quote
                          .modifiedCoveragePremium ?? 0,
                      fusionScoreModifier:
                        endorsementProps.uuid ===
                        EndorsementUuid.TrackingTechnologyLiabilityCoverageEndorsement
                          ? 1.5
                          : limitAndRetentionCoverage?.quote
                              .fusionScoreModifier ?? 0,
                      minFusionScoreModifier:
                        endorsementProps.uuid ===
                        EndorsementUuid.TrackingTechnologyLiabilityCoverageEndorsement
                          ? 1.0
                          : limitAndRetentionCoverage?.quote
                              .minFusionScoreModifier ?? 0,
                      maxFusionScoreModifier:
                        endorsementProps.uuid ===
                        EndorsementUuid.TrackingTechnologyLiabilityCoverageEndorsement
                          ? 3.0
                          : limitAndRetentionCoverage?.quote
                              .maxFusionScoreModifier ?? 0,
                    },
                    isNew: true,
                  };

                  dispatch(
                    actions.addEndorsement({
                      features: QuoteLineItemFeatures.from(
                        QuoteLineItemType.Endorsement,
                        newEndorsement
                      ),
                      ...newEndorsement,
                    })
                  );
                }
              }}
            />
          </TableCell>
          <TableCell colSpan={columnCount - 3}></TableCell>
        </TableRow>
      )}
      <RaterQuoteSaveDialog
        show={openSaveDialog}
        title={t(
          'underwriting.quote.saveDialog.title',
          'Save Coverage & Rating'
        )}
        content={t(
          'underwriting.quote.saveDialog.content',
          'Please save your changes before adding a note'
        )}
        onConfirm={() => {
          setOpenSaveDialog(false);
        }}
        onCancel={() => {
          setOpenSaveDialog(false);
        }}
      />
    </Fragment>
  );
};
